// src/utils/popup/PopupContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import styles from "./Popup.module.css"; // 👈 CSS Module

const PopupContext = createContext(null);

const THEME = {
  success: { Icon: CheckCircle2, color: "#16a34a" },
  error:   { Icon: XCircle,      color: "#dc2626" },
  warning: { Icon: AlertTriangle,color: "#f59e0b" },
  info:    { Icon: Info,         color: "#2563eb" },
};

export function usePopup() {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error("usePopup must be used within <PopupProvider>");
  return ctx;
}

export function PopupProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    autocloseMs: null,
  });
  const [closing, setClosing] = useState(false);

  const hidePopup = useCallback(() => {
    // setClosing(true);
    // setTimeout(() => {
      setState((s) => ({ ...s, open: false, autocloseMs: null }));
      setClosing(false);
    // }, 5000); // match CSS animation
  }, []);

  const showPopup = useCallback((opts) => {
    setClosing(false);
    setState({
      open: true,
      title: opts.title || "Notice",
      message: opts.message || "",
      variant: opts.variant || "info",
      autocloseMs: opts.autocloseMs ?? null,
    });
  }, []);

  // body scroll lock
  useEffect(() => {
    if (state.open || closing) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [state.open, closing]);

  // ESC to close
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => e.key === "Escape" && hidePopup();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open, hidePopup]);

  // Autoclose
  useEffect(() => {
    if (!state.open || !state.autocloseMs) return;
    const t = setTimeout(hidePopup, state.autocloseMs);
    return () => clearTimeout(t);
  }, [state.open, state.autocloseMs, hidePopup]);

  const ctxValue = useMemo(() => ({ showPopup, hidePopup }), [showPopup, hidePopup]);

  if (!state.open && !closing) {
    return <PopupContext.Provider value={ctxValue}>{children}</PopupContext.Provider>;
  }

  const { Icon, color } = THEME[state.variant] || THEME.info;

  return (
    <PopupContext.Provider value={ctxValue}>
      {children}

      <div
        className={`${styles.modalBackdrop} ${closing ? styles.modalBackdropClosing : ""}`}
        onClick={hidePopup}
        role="dialog"
        aria-modal="true"
        aria-label={state.title || "Notice"}
      >
        <div
          className={`${styles.modalSheet} ${closing ? styles.modalSheetClosing : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalGrip} aria-hidden="true" />

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <Icon size={22} color={color} aria-hidden="true" />
            <h4 style={{ margin: 0 }}>{state.title}</h4>
          </div>

          <p style={{ marginTop: 8, marginBottom: 16 }}>{state.message}</p>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className={styles.modalCloseBtn} onClick={hidePopup}>OK</button>
          </div>
        </div>
      </div>
    </PopupContext.Provider>
  );
}
